import type { UploadFile } from 'antd'
import dayjs from 'dayjs'
import { useMutation, useQueryClient } from 'react-query'
import { postJSON } from '../../../../../common/services/api'
import { showToast } from '../../../../../common/utils'
import { uploadDocuments } from '../../../../../common/utils/documentUpload'
import { COMMUNICATION_RECORDS_CATEGORY_ID } from '../constants'
import {
  COMMUNICATION_RECORDS_QUERY_KEY,
  COMMUNICATION_RECORDS_URL,
  type CreateCommunicationRecordBody
} from './useCommunicationRecordCreate'

type UseCommunicationRecordUpdateProps = {
  onSuccess?: () => void
}

export interface UpdateCommunicationRecordInput
  extends CreateCommunicationRecordBody {
  _id: string
  existingDocumentIds?: string[]
}

type UseCommunicationRecordUpdateReturn = {
  updateCommunicationRecord: (
    values: UpdateCommunicationRecordInput,
    fileList?: UploadFile[]
  ) => Promise<unknown>
  isUpdateLoading: boolean
}

export const useCommunicationRecordUpdate = ({
  onSuccess
}: UseCommunicationRecordUpdateProps): UseCommunicationRecordUpdateReturn => {
  const queryClient = useQueryClient()

  const handleError = (err: any) => {
    showToast({
      title: (err?.message || err?.msg || 'Something went wrong') as string,
      type: 'error'
    })
  }

  const handleSuccess = () => {
    showToast({
      title: 'Updated Document',
      type: 'success'
    })
  }

  const updateFunction = async (
    values: UpdateCommunicationRecordInput,
    fileList?: UploadFile[]
  ) => {
    const { _id, existingDocumentIds, ...updateFields } = values

    if (!_id) {
      throw new Error('Record ID is required for update')
    }

    const hasFiles = fileList && fileList.length > 0

    let documentIds: string[] | undefined

    // Upload new documents using presign/commit flow if files are provided
    if (hasFiles) {
      try {
        // Get existing document IDs from input
        const existingIds = existingDocumentIds || []

        // Upload new documents
        const newDocumentIds = await uploadDocuments(
          fileList,
          updateFields.resident,
          COMMUNICATION_RECORDS_CATEGORY_ID
        )

        // Combine existing and new document IDs
        documentIds = [...existingIds, ...newDocumentIds]
      } catch (error) {
        // Preserve the real cause so Sentry's LinkedErrors surfaces the
        // underlying S3/network failure (CM-3346).
        throw new Error('Failed to upload documents', { cause: error })
      }
    }

    const formattedValues = {
      ...updateFields,
      ...(updateFields.dateSent && {
        dateSent: dayjs.isDayjs(updateFields.dateSent)
          ? updateFields.dateSent.toISOString()
          : dayjs(updateFields.dateSent).toISOString()
      }),
      // Include documents field if new files were uploaded (merged with existing)
      ...(documentIds !== undefined && { documents: documentIds })
    }

    return postJSON({
      url: `${COMMUNICATION_RECORDS_URL}/${_id}`,
      method: 'PUT',
      json: formattedValues
    })
  }

  const { mutateAsync: updateMutation, isLoading: isUpdateLoading } =
    useMutation<
      unknown,
      unknown,
      { values: UpdateCommunicationRecordInput; fileList?: UploadFile[] },
      unknown
    >(
      [COMMUNICATION_RECORDS_QUERY_KEY, 'update'],
      ({ values, fileList }) => updateFunction(values, fileList),
      {
        onSuccess: () => {
          handleSuccess()
          queryClient.invalidateQueries([COMMUNICATION_RECORDS_QUERY_KEY])
          onSuccess?.()
        },
        onError: (err) => handleError(err)
      }
    )

  const updateCommunicationRecord = async (
    values: UpdateCommunicationRecordInput,
    fileList?: UploadFile[]
  ) => {
    try {
      const result = await updateMutation({ values, fileList })
      return result
    } catch (err) {
      return err
    }
  }

  return {
    updateCommunicationRecord,
    isUpdateLoading
  }
}
